// The Sales node, shared by both network diagrams. Outflow bands converge
// on the node as a whole — deliberately NOT one band per channel, which at
// 4+ locations × 5+ channels is unreadable. The split lives inside the node
// as a stacked list instead.
import {
  SnapshotChannel,
  SnapshotPeriod,
  SnapshotSales,
} from "./types";
import {
  CHANNEL_RAMP,
  EM_DASH,
  SALES_NODE_TRACK,
  SnapshotTokens,
  un,
  usd,
} from "./primitives";

export const SALES_NODE_WIDTH = 208;

/** Height grows with the channel list so nothing is clipped. */
export const salesNodeHeight = (channelCount: number): number =>
  110 + Math.max(0, channelCount) * 15;

export const SalesNode = ({
  t,
  x,
  centerY,
  sales,
  channels,
  period,
}: {
  t: SnapshotTokens;
  x: number;
  centerY: number;
  sales: SnapshotSales;
  channels: SnapshotChannel[];
  period: SnapshotPeriod;
}) => {
  const isMonth = period === "month";
  // The month figure is a to-date total, not a monthly rate — say so.
  const per = isMonth ? "· month to date" : "/ week";
  const h = salesNodeHeight(channels.length);
  const top = centerY - h / 2;
  const maxShare = Math.max(1e-9, ...channels.map((c) => c.share));
  const barX = x + 132;
  const barW = 58;

  return (
    <g>
      <rect
        x={x}
        y={top}
        width={SALES_NODE_WIDTH}
        height={h}
        rx="11"
        fill={t.green1}
        stroke={t.greenInk}
        strokeOpacity="0.35"
        strokeWidth="1.6"
      >
        <title>Outflow from the whole network to customers</title>
      </rect>
      <text
        x={x + 16}
        y={top + 22}
        fontSize="9.5"
        fontWeight="700"
        letterSpacing="0.6"
        fill={t.greenInk}
      >
        SALES
      </text>
      <text x={x + 16} y={top + 48} fontSize="22" fontWeight="700" fill={t.ink}>
        {sales.weeklyUnits == null ? EM_DASH : un(sales.weeklyUnits)}
      </text>
      <text x={x + 16} y={top + 63} fontSize="10" fill={t.ink3}>
        units shipped {per}
      </text>
      <text
        x={x + 16}
        y={top + 84}
        fontSize="13"
        fontWeight="700"
        fill={t.ink}
      >
        {sales.weeklyRevenue == null ? EM_DASH : usd(sales.weeklyRevenue, 2)}
        <tspan fontSize="10" fontWeight="400" fill={t.ink3}>
          {"  revenue " + per}
        </tspan>
      </text>

      {/* Channel split — color chip, label, units, share bar. */}
      {channels.map((c, i) => {
        const rowY = top + 104 + i * 15;
        const color = c.isOther ? t.ink4 : CHANNEL_RAMP[i % CHANNEL_RAMP.length];
        return (
          <g key={c.channel}>
            <title>
              {`${c.channel}: ${Math.round(c.units).toLocaleString("en-US")} units, ${(
                c.share * 100
              ).toFixed(1)}% of outflow`}
            </title>
            <rect
              x={x + 16}
              y={rowY - 7}
              width="7"
              height="7"
              rx="1.5"
              fill={color}
            />
            <text x={x + 28} y={rowY} fontSize="9.5" fill={t.ink2}>
              {c.channel.length > 13
                ? c.channel.slice(0, 12).trimEnd() + "…"
                : c.channel}
            </text>
            <text
              x={x + 126}
              y={rowY}
              fontSize="9.5"
              fontWeight="600"
              fill={t.ink2}
              textAnchor="end"
            >
              {un(c.units)}
            </text>
            {/* Track is darkened for contrast against the green node. */}
            <rect
              x={barX}
              y={rowY - 6}
              width={barW}
              height="5"
              rx="2.5"
              fill={SALES_NODE_TRACK}
            />
            <rect
              x={barX}
              y={rowY - 6}
              width={Math.max(1, (c.share / maxShare) * barW)}
              height="5"
              rx="2.5"
              fill={color}
            />
          </g>
        );
      })}

      <text
        x={x + 16}
        y={top + h - 12}
        fontSize="9.5"
        fontWeight="600"
        fill={t.greenInk}
      >
        {sales.turnRate == null
          ? EM_DASH
          : isMonth
          ? `${sales.turnRate.toFixed(2)}× of stock turns monthly`
          : `${(sales.turnRate * 100).toFixed(1)}% of stock turns weekly`}
      </text>
    </g>
  );
};
